import React, { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Avatar, Grid, IconButton, Theme } from '@material-ui/core';
import SchoolIcon from '@material-ui/icons/School';
import ListIcon from '@material-ui/icons/List';
import AssignmentIcon from '@material-ui/icons/Assignment';
import { Button } from '../../../common/button/Button';
import colors from '../../../../../styles/colors';
import { ordinalSuffix } from '../../../../../utils/grade';
import { AssignmentEditStatus } from '../../edit/state/assignment-edit.types';
import { show } from 'redux-modal/lib/actions';
import { MODAL_ASSIGN_USER_GENERATED_CONTENT } from '../../../../../constants/modals';
import { useDispatch } from 'react-redux';
import { Edit } from '@material-ui/icons';

interface AssignmentData {
  id: number;
  name: string;
  isValid: boolean;
  dashboardUserId: number;
  grade: number;
  shuffleAnswers: boolean;
  shuffleQuestions: boolean;
  startDate: Date;
  email: string;
  isAssigned: boolean;
  status: string;
  endDate: Date;
  updatedAt: Date;
  createdAt: Date;
  totalQuestions: number;
  totalPassageQuestions: number;
  totalIndependentQuestions: number;
  color?: string;
  type?: string;
}

export interface AssignmentCardProps {
  assignment: AssignmentData;
  userClasses: any[];
  handleAssign: (
    assignment: any,
    userClasses: any,
    dashboardUserId: number,
    assignmentId: number,
    isFromEdit: boolean
  ) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    border: '1px solid #DDDDDD',
    position: 'relative',
    borderRadius: '4px',
    minHeight: '305px'
  },
  header: {
    backgroundColor: (props: any) => props.backgroundColor,
    borderRadius: '4px 4px 0px 0px',
    color: 'white',
    position: 'relative',
    display: 'flex'
  },
  tag: {
    position: 'absolute',
    right: 10,
    bottom: -10,
    fontSize: '11px',
    color: 'white',
    backgroundColor: (props: any) => {
      if (props.type) {
        if (props.type === 'TEST PREP') {
          return '#4BD1D8';
        } else {
          return '#19222D';
        }
      } else {
        return '#C42121';
      }
    },
    borderRadius: '4px',
    padding: '2px 5px'
  },
  avatar: {
    backgroundColor: (props: any) => props.avatar,
    width: theme.spacing(3),
    height: theme.spacing(3),
    fontSize: '11px'
  },
  alignMiddle: {
    display: 'flex',
    alignItems: 'center'
  },
  action: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center'
  },
  questionMetaData: {
    display: 'flex',
    alignItems: 'flex-start',
    margin: '.8rem 0rem'
  },
  questionCount: {
    fontSize: '.7rem'
  },
  spaceLeft: {
    marginLeft: '10px'
  },
  spaceTopBottom: {
    marginTop: '10px',
    marginBottom: '10px'
  },
  padding: {
    padding: '.8rem 1.2rem'
  },
  content: {
    height: 'auto'
  },
  assigned: {
    color: props => props.assigned
  }
}));

const AssignmentCard: React.FC<AssignmentCardProps> = (
  props: PropsWithChildren<AssignmentCardProps>
) => {
  const {
    name,
    id,
    color,
    email,
    isAssigned,
    totalPassageQuestions,
    status,
    isValid,
    totalIndependentQuestions,
    totalQuestions,
    grade,
    type
  } = props.assignment;
  const isPremade = type;
  const totalNumberOfQuestions = totalQuestions;
  const independentQuestionsCount = totalIndependentQuestions;
  const passageQuestionsCount = totalPassageQuestions;
  const styles = {
    backgroundColor: color,
    avatar: '#C170A1',
    assigned: isAssigned || isPremade ? 'green' : 'red',
    type: type
  };
  const classes = useStyles(styles);
  const initials = email && email[0].toUpperCase();
  const gradeString = ordinalSuffix(grade as number);
  const handleAssign = (evt: MouseEvent) => {
    evt.stopPropagation();
    props.handleAssign(
      props.assignment,
      props.userClasses,
      props.assignment.dashboardUserId,
      props.assignment.id,
      true
    );
  };

  let tagString = status;
  if (isPremade) {
    if (type === 'TEST PREP') {
      tagString = 'TEST PREP';
    } else {
      tagString = 'PREMADE';
    }
  }

  return (
    <Grid container className={classes.root}>
      <Grid item xs={12}>
        <Grid container className={`${classes.header}  ${classes.padding}`}>
          <Grid item xs={12} style={{ justifyContent: 'space-between' }}>
            <b
              title={name}
              style={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                display: 'inline-block',
                width: '80%',
                whiteSpace: 'nowrap'
              }}
            >
              {name}
            </b>
            {(status === AssignmentEditStatus.Draft || isPremade) && (
              <b className={classes.tag}>{tagString}</b>
            )}
          </Grid>
          <Grid
            className={`${classes.spaceTopBottom} ${classes.alignMiddle}`}
            item
            xs={12}
          >
            <SchoolIcon fill="red" fontSize={'small'} />
            <small className={classes.spaceLeft}>{gradeString} Grade</small>
          </Grid>
          <Grid item xs={12} className={classes.alignMiddle}>
            <Avatar className={classes.avatar}>{initials}</Avatar>
            <small
              className={classes.spaceLeft}
              title={email}
              style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
            >
              {email}
            </small>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <div className={`${classes.content}  ${classes.padding}`}>
          {/*<div className={classes.alignMiddle}>*/}
          {/*  <AssignmentIcon />*/}
          {/*  <small className={classes.spaceLeft}>Adverbs, Close Reading</small>*/}
          {/*</div>*/}
          <div className={classes.questionMetaData}>
            <ListIcon />
            <div className={classes.spaceLeft}>
              <small>{totalNumberOfQuestions} Questions in Total</small>
              <br />
              <ul>
                <li>
                  <small className={classes.questionCount}>
                    {independentQuestionsCount} Independent Questions
                  </small>
                </li>
                <li>
                  <small className={classes.questionCount}>
                    {passageQuestionsCount} Passage Questions
                  </small>
                </li>
              </ul>
            </div>
          </div>
          <div className={classes.alignMiddle}>
            <AssignmentIcon />
            <small className={`${classes.assigned} ${classes.spaceLeft}`}>
              {isAssigned || isPremade ? 'Assigned' : 'Unassigned'}
            </small>
          </div>
        </div>
      </Grid>
      <Grid item xs={12} className={`${classes.action} ${classes.padding}`}>
        <Button
          color={colors.assignment.blue}
          variant={'outline'}
          textColor={colors.assignment.blue}
        >
          {isPremade ? 'View' : 'Edit'}
        </Button>
        &nbsp;
        {!isPremade && (
          <Button
            title={!isValid && !isPremade ? 'Assignment contains errors.' : ''}
            variant={'filled'}
            textColor="white"
            disabled={
              (!isValid && !isPremade) || (!isPremade && totalQuestions === 0)
            }
            onClick={handleAssign}
            color={colors.assignment.blue}
          >
            Assign
          </Button>
        )}
      </Grid>
    </Grid>
  );
};

export default AssignmentCard;
